import { type FC, type PropsWithChildren, useEffect, useLayoutEffect } from 'react';
import classNames from 'classnames';

import { tm } from '@web-solutions/module-localization';
import Analytics from '@web-solutions/module-analytics';

import { EVENT_ACTION } from '@web-solutions/core/constants/general';

import { ReactComponent as IconClose } from '@web-solutions/core/payment/base/icons/close-icon.svg';
import { Modal, Button } from '@web-solutions/core/ui-elements';
import PaymentDescription from '@web-solutions/core/payment/components/payment-description';
import { PolicyText } from '@web-solutions/core/summary/plans/policy-text';
import { PaymentSafe } from '@web-solutions/core/containers/payment-safe';

import { useRemoteConfig } from '@web-solutions/base-app/hooks/use-remote-config';

import { tKey } from '../constants'

import { type SpecialOfferBaseProps } from '../types'

import { SpecialOfferExpired } from './offer-expired';

import { useGetMaxSOModalHeight } from './hooks'

import styles from './style.module.scss';

export const SpecialOfferBase: FC<PropsWithChildren<SpecialOfferBaseProps>> = ({
  mode,
  isOpen,
  isCloseOnSubmit = false,
  withPaymentDescription = false,
  withCloseButton = true,
  withTerms = false,
  className,
  activeProduct,
  children,
  buttonTitle,
  onGetClick,
  onClose,
}) => {
  const { showTermsAtPlans, showPaymentSafeAtPlans } = useRemoteConfig()
  const { modalHeight, ref } = useGetMaxSOModalHeight({ isAvailable: mode === 'with_plans_v2', additionalHeight: 72 })

  useEffect(() => {
    if (isOpen) {
      Analytics.trackEvent('special_offer', EVENT_ACTION.OPEN, { mode });
    }
  }, [isOpen, mode])

  const analyticsParams = { productId: activeProduct?.id, mode }

  const handleCloseClick = () => {
    Analytics.trackEvent('special_offer', EVENT_ACTION.CLOSE, analyticsParams);
    onClose()
  }

  const handleSubmitClick = () => {
    Analytics.trackEvent('special_offer', isCloseOnSubmit ? EVENT_ACTION.CLOSE : EVENT_ACTION.SUBMIT, analyticsParams);
    onGetClick();
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        maxModalHeight={showTermsAtPlans && modalHeight ? modalHeight : null}
        className={classNames(styles.modalContainer, className)}
      >
        {withCloseButton &&
          <button type="button" onClick={handleCloseClick} className={styles.close}>
            <IconClose className={styles.close_icon} />
          </button>
        }
        <div ref={ref} className={styles.mainContentWrapper} >
          {children}
          <Button
            title={tm(buttonTitle, `${tKey}${mode === 'with_plans' || mode === 'with_plans_v2' ? '' : `.${mode}`}.btn`)}
            onClick={handleSubmitClick}
            className={styles.btn}
          />
        </div>
        {withPaymentDescription &&
          <PaymentDescription activeProduct={activeProduct!} />
        }
        {withTerms &&
          <>
            <PolicyText className={styles.policyText} />
            {
              showTermsAtPlans &&
              <PaymentDescription
                isShort
                className={styles.paymentDescription}
                activeProduct={activeProduct!}
              />
            }
            {showPaymentSafeAtPlans &&
              <PaymentSafe
                isModal={false}
                className={styles.paymentSafe}
              />
            }
          </>
        }
      </Modal>
      <SpecialOfferExpired mode={mode} isSOOpen={isOpen} onSOClose={onClose} />
    </>
  );
};